import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Radar, Lock, Unlock } from "lucide-react";
import { ConfirmModal, ActiveBadge, BTN_PRIMARY } from "components/admin/AdminUI";
import api from "lib/api";

export default function Crawlers() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/crawlers/");
      setList(data.results || data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function confirmDelete() {
    setBusy(true);
    try {
      await api.delete(`/admin/crawlers/${deleting.id}/`);
      setDeleting(null);
      load();
    } finally {
      setBusy(false);
    }
  }

  async function confirmToggle() {
    setBusy(true);
    try {
      await api.patch(`/admin/crawlers/${toggling.id}/`, { is_active: !toggling.is_active });
      setToggling(null);
      load();
    } finally {
      setBusy(false);
    }
  }

  const Actions = ({ c }) => (
    <div className="flex items-center gap-1">
      <button
        onClick={() => navigate(`/admin/crawlers/${c.id}/edit`)}
        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Editar"
      >
        <Pencil className="h-4 w-4" />
      </button>
      <button
        onClick={() => setToggling(c)}
        className={`rounded p-1.5 text-muted-foreground transition-colors ${
          c.is_active
            ? "hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
            : "hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-900/20"
        }`}
        title={c.is_active ? "Desativar" : "Ativar"}
      >
        {c.is_active ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
      </button>
      <button
        onClick={() => setDeleting(c)}
        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
        title="Excluir"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );

  return (
    <div>
      <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Crawlers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            IPs de varredura exibidos na página pública <code className="font-mono">/scan</code>.
          </p>
        </div>
        <button onClick={() => navigate("/admin/crawlers/new")} className={BTN_PRIMARY}>
          <Plus className="h-4 w-4" /> Novo IP
        </button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center">
            <Radar className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Nenhum IP cadastrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">IP / CIDR</th>
                  <th className="px-4 py-3 font-medium">Hostname</th>
                  <th className="px-4 py-3 font-medium">Descrição</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="border-b border-border transition-colors last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3 font-mono font-medium">{c.ip}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.hostname || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.description || "—"}</td>
                    <td className="px-4 py-3"><ActiveBadge active={c.is_active} activeLabel="Ativo" /></td>
                    <td className="px-4 py-3"><div className="flex justify-end"><Actions c={c} /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleting && (
        <ConfirmModal
          icon={Trash2}
          iconClass="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
          title="Excluir IP de varredura?"
          confirmLabel="Excluir"
          confirmClass="bg-red-600 text-white hover:bg-red-700"
          loading={busy}
          onClose={() => setDeleting(null)}
          onConfirm={confirmDelete}
        >
          Tem certeza que deseja excluir <strong>{deleting.ip}</strong>?
        </ConfirmModal>
      )}

      {toggling && (
        <ConfirmModal
          icon={toggling.is_active ? Lock : Unlock}
          iconClass={toggling.is_active
            ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
            : "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"}
          title={toggling.is_active ? "Desativar IP?" : "Ativar IP?"}
          confirmLabel={toggling.is_active ? "Desativar" : "Ativar"}
          confirmClass={toggling.is_active
            ? "bg-red-600 text-white hover:bg-red-700"
            : "bg-emerald-600 text-white hover:bg-emerald-700"}
          loading={busy}
          onClose={() => setToggling(null)}
          onConfirm={confirmToggle}
        >
          {toggling.is_active
            ? "O IP deixará de aparecer na página /scan."
            : "O IP voltará a aparecer na página /scan."}
        </ConfirmModal>
      )}
    </div>
  );
}
